import { useState, useRef, useCallback, useEffect } from 'react';
import { transcribeKianVoice } from '../lib/api/domains/kianVoice';
import { useKianVoiceConfig } from '../components/KianAssistant/voice/useKianVoiceConfig';
import {
  createAudioContext,
  isCaptureSupported,
  startCapture,
  type CaptureSession,
} from '../components/KianAssistant/voice/audioCapture';

type VoiceInputStatus = 'idle' | 'listening' | 'transcribing' | 'error';

interface UseVoiceInputResult {
  supported: boolean;
  status: VoiceInputStatus;
  error: string | null;
  /** Diktat läuft über den Server (Sprachmodell) statt über den Browser */
  serverVoice: boolean;
  startListening: () => void;
  stopListening: () => void;
}

// Browser SpeechRecognition — not in all TS lib versions, declared locally
interface SpeechRecognitionInstance extends EventTarget {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionInstance;

/**
 * Scheiterte das Server-Diktat (z. B. Modell falsch eingestellt), diktiert diese Sitzung wieder
 * über den Browser — das Mikrofon, das bisher ging, soll nicht wegen der neuen Einstellung ausfallen.
 */
let serverDictationFailed = false;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === 'undefined') return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? undefined;
}

/**
 * Diktat ins Eingabefeld.
 *
 * Mit eingerichteter KIAN-Sprache (Einstellungen → Sprachmodell) nimmt der Browser selbst auf und
 * der Server wandelt um — geht auf jedem Gerät, erkennt Kundennamen und Fachbegriffe besser, und
 * die Aufnahme läuft über denselben Anbieter wie KIAN statt über Google (Chrome) bzw. Microsoft
 * (Edge). Ohne Einrichtung bleibt die Spracherkennung des Browsers (nur Chrome/Edge).
 */
export function useVoiceInput(onTranscript: (text: string) => void): UseVoiceInputResult {
  const [status, setStatus] = useState<VoiceInputStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
  const captureRef = useRef<CaptureSession | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const mountedRef = useRef(true);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const voiceConfig = useKianVoiceConfig();
  const [serverBroken, setServerBroken] = useState(serverDictationFailed);
  const Ctor = getSpeechRecognitionCtor();
  const serverVoice = voiceConfig.data?.available === true && isCaptureSupported() && !(serverBroken && Ctor);
  const supported = serverVoice || Boolean(Ctor);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      recognitionRef.current?.abort();
      captureRef.current?.cancel();
      void ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
    };
  }, []);

  const startServer = useCallback(async () => {
    setError(null);
    try {
      // Aus dem Klick heraus anlegen — Safari lässt Audio sonst nicht zu
      if (!ctxRef.current || ctxRef.current.state === 'closed') ctxRef.current = createAudioContext();
      const session = await startCapture(ctxRef.current, { silenceMs: 1600, noSpeechMs: 10_000, maxMs: 90_000 });
      captureRef.current = session;
      if (mountedRef.current) setStatus('listening');
      const recorded = await session.done;
      captureRef.current = null;
      if (!mountedRef.current) return;
      if (!recorded.wav) {
        setStatus('idle');
        return;
      }
      setStatus('transcribing');
      let res: Awaited<ReturnType<typeof transcribeKianVoice>>;
      try {
        res = await transcribeKianVoice(recorded.wav);
      } catch (err) {
        // Nächstes Mal über den Browser — sofern er es kann
        serverDictationFailed = true;
        if (mountedRef.current) setServerBroken(true);
        throw err;
      }
      if (!mountedRef.current) return;
      if (res.text.trim()) onTranscriptRef.current(res.text.trim());
      setStatus('idle');
    } catch (err) {
      if (!mountedRef.current) return;
      const msg =
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Kein Zugriff aufs Mikrofon — bitte im Browser erlauben.'
          : err instanceof Error
            ? err.message
            : 'Spracheingabe fehlgeschlagen';
      setError(msg);
      setStatus('error');
    }
  }, []);

  const startBrowser = useCallback(() => {
    const C = getSpeechRecognitionCtor();
    if (!C) return;
    setError(null);

    const recognition = new C();
    recognition.lang = 'de-DE';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      if (mountedRef.current) setStatus('listening');
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      const transcript = event.results[0]?.[0]?.transcript ?? '';
      if (transcript && mountedRef.current) {
        onTranscriptRef.current(transcript);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (!mountedRef.current) return;
      if (event.error !== 'aborted') {
        setError('Spracherkennung fehlgeschlagen');
        setStatus('error');
      } else {
        setStatus('idle');
      }
    };

    recognition.onend = () => {
      if (mountedRef.current) setStatus((s) => (s === 'error' ? s : 'idle'));
    };

    recognition.start();
  }, []);

  const startListening = useCallback(() => {
    if (serverVoice) void startServer();
    else startBrowser();
  }, [serverVoice, startServer, startBrowser]);

  const stopListening = useCallback(() => {
    // Server-Diktat: aufhören und auswerten („Fertig gesprochen")
    captureRef.current?.stop();
    recognitionRef.current?.stop();
    recognitionRef.current = null;
  }, []);

  return { supported, status, error, serverVoice, startListening, stopListening };
}
